import { supabase } from "@/integrations/supabase/client";
import { type User, type UserProfile } from "@/types";
import { Capacitor } from '@capacitor/core';
import { logActivity, ActivityActions } from "@/utils/activityLogger";
import { PostgrestSingleResponse } from '@supabase/supabase-js';
import { quickConnectivityCheck } from '@/integrations/supabase/client';
// ✅ FIX: Import OneSignal statically at the top to avoid React hook context issues
import { initializeOneSignalWithProperExternalId } from '@/integrations/onesignal';

// Global resume state tracking - this will be updated by the resume manager
let isAppResuming = false;
let isNetworkReady = true;
let pendingApiCalls: Array<() => Promise<any>> = [];

// Function to update resume state from useResumeManager
export const updateResumeState = (resuming: boolean, networkReady: boolean) => {
  const previousResuming = isAppResuming;
  const previousNetworkReady = isNetworkReady;
  
  isAppResuming = resuming;
  isNetworkReady = networkReady;
  
  // If we were resuming and now network is ready, execute pending API calls
  if (previousResuming && !isAppResuming && networkReady) {
    
    executePendingApiCalls();
  }
  
  // If network was not ready and now is ready, execute pending API calls
  if (!previousNetworkReady && networkReady) {
    
    executePendingApiCalls();
  }
};

// Execute pending API calls and clear the queue
const executePendingApiCalls = () => {
  const callsToExecute = [...pendingApiCalls];
  pendingApiCalls = [];
  
  // Execute each call without awaiting - they'll resolve on their own
  callsToExecute.forEach(apiCall => {
    apiCall().catch(error => {
      
    });
  });
};

// Function to safely execute API calls with resume protection
export const safeApiCall = async <T>(
  apiCallFn: () => Promise<T>,
  options: {
    timeoutMs?: number,
    name?: string,
    critical?: boolean, // If true, will execute even during resume
    fallbackValue?: T,  // Optional fallback value to return if call can't be made
    queueIfResuming?: boolean // If true, will queue the call to execute after resume
  } = {}
): Promise<T> => {
  const {
    timeoutMs = 10000,
    name = "unnamed_api_call",
    critical = false,
    queueIfResuming = true,
    fallbackValue
  } = options;
  
  // On iOS, if app is resuming and network isn't ready yet
  if (Capacitor.isNativePlatform() && 
      Capacitor.getPlatform() === 'ios' && 
      isAppResuming && 
      !isNetworkReady && 
      !critical) {
    
    
    
    // If queueing is enabled, add to pending calls
    if (queueIfResuming) {
      
      
      // Return a promise that will resolve once we can execute the call
      return new Promise((resolve, reject) => {
        pendingApiCalls.push(async () => {
          try {
            
            const result = await withTimeout(apiCallFn(), timeoutMs, name);
            resolve(result);
            return result;
          } catch (error) {
            
            reject(error);
            throw error;
          }
        });
      });
    }
    
    // If fallback value is provided, return it
    if (fallbackValue !== undefined) {
      
      return fallbackValue;
    }
    
    // Otherwise throw an error
    throw new Error(`API call "${name}" blocked due to app resuming from background`);
  }
  
  // Normal flow - execute with timeout
  return withTimeout(apiCallFn(), timeoutMs, name);
};

// Create a helper function for API calls with timeout
// Use PromiseLike<T> to accept Supabase builders which are "thenable"
const withTimeout = <T>(promise: PromiseLike<T>, timeoutMs: number, name: string): Promise<T> => {
  
  // Wrap the PromiseLike in Promise.resolve() to ensure we have a standard Promise
  const standardPromise = Promise.resolve(promise);

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      
      reject(new Error(`API call timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    // Use the standardized promise here
    standardPromise.then(
      (result) => {
        clearTimeout(timeoutId);
        
        resolve(result);
      },
      (error) => {
        clearTimeout(timeoutId);
        
        reject(error);
      }
    );
  });
};

// Create a lock mechanism to prevent parallel getCurrentUser calls
let getCurrentUserInProgress = false;
let getCurrentUserLastAttemptTime = 0;

// Function to check if the network is ready for API calls
export const isNetworkReadyForApiCalls = (): boolean => {
  if (Capacitor.isNativePlatform() && 
      Capacitor.getPlatform() === 'ios' && 
      isAppResuming && 
      !isNetworkReady) {
    return false;
  }
  return true;
};

// Auth API
export const authAPI = {
  // Sign up a new user using phone number or email
  async signUp(identifier: string, password: string, userData?: { name?: string, company?: string, email?: string, country?: string, city?: string, role?: string }, captchaToken?: string) {
    // Determine if identifier is an email or phone
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);

    const signUpData = isEmail
      ? { email: identifier, password } as any
      : { phone: identifier, password } as any;

    if (userData || captchaToken) {
      signUpData.options = {};
      if (userData) signUpData.options.data = userData;
      if (captchaToken) signUpData.options.captchaToken = captchaToken;
    }
    
    const result = await safeApiCall(
      () => supabase.auth.signUp(signUpData),
      {
        timeoutMs: 20000,
        name: "signUp",
        critical: true
      }
    );
    
    if (result.data?.user) {
      await logActivity(
        result.data.user.id,
        ActivityActions.SIGNUP,
        isEmail ? { email: identifier } : { phone: identifier }
      );

      // Initialize OneSignal with FIXED External ID binding for new user
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          const newUserId = result.data.user.id; // Store user ID to avoid variable conflict
          
          // ✅ FIX: Use setTimeout with static import to avoid React hook context issues
          setTimeout(async () => {
            // Wait for user session to be established
            await new Promise(resolve => setTimeout(resolve, 1500));
            try {
              // Step 1: Initialize OneSignal with proper External ID binding
              // Use static import instead of dynamic import to avoid hook context issues
              const osResult = await initializeOneSignalWithProperExternalId(newUserId);
              
              
              
              // Step 2: For new users, also request permission with welcome UI
              if (osResult.success) {
                // Wait additional time for the first initialization to complete
                await new Promise(resolve => setTimeout(resolve, 1000));
                try {
                  const { enhancedOneSignalLogin } = await import('@/integrations/onesignal/enhanced-auth-flow');
                  await enhancedOneSignalLogin(newUserId, {
                    requestPermissionImmediately: true,
                    silentMode: false,
                    showToast: undefined, // ✅ FIX: No toast callback available in API context - keep silent
                    customPermissionRequest: {
                      title: "Welcome to SantiyeX! 🎉",
                      message: "Enable notifications to receive important updates about your projects and payments.",
                      allowButtonText: "Enable Notifications",
                      denyButtonText: "Maybe Later"
                    }
                  });
                } catch (permissionError) {
                  
                }
              }
            } catch (error) {
              
            }
          }); // Slightly longer delay for new users
        }
      } catch (onesignalError) {
        
        // Don't fail the signup if OneSignal fails
      }
    }
    
    return result;
  },

  // Sign in a user using phone number or email
  async signIn(identifier: string, password: string, captchaToken?: string) {
    try {
      // Determine if identifier is an email or phone
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);

      const signInData = isEmail
        ? { email: identifier, password } as any
        : { phone: identifier, password } as any;

      if (captchaToken) {
        signInData.options = { captchaToken };
      }
      
      const result = await safeApiCall(
        () => supabase.auth.signInWithPassword(signInData),
        {
          timeoutMs: 20000,
          name: "signIn",
          critical: true
        }
      );
      
      if (result.data?.user) {
        await logActivity(
          result.data.user.id,
          ActivityActions.LOGIN,
          { timestamp: new Date().toISOString() }
        );
        
        try {
          const { currencyAPI } = await import("../api/currency");
          
          await currencyAPI.getExchangeRates();
        } catch (ratesError) {
          
        }
      }
      
      return result;
    } catch (error) {
      
      throw error;
    }
  },

  // Sign out the current user
  async signOut() {
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      
      const result = await safeApiCall(
        () => supabase.auth.signOut(),
        {
          timeoutMs: 10000,
          name: "signOut",
          critical: true
        }
      );
      
      if (userId) {
        await logActivity(
          userId,
          ActivityActions.LOGOUT,
          { timestamp: new Date().toISOString() }
        );
      }
      
      return result;
    } catch (error) {
      
      return { error: null };
    }
  },

  // Get the current user session
  async getSession() {
    
    try {
      const session = await safeApiCall(
        () => supabase.auth.getSession(),
        {
          timeoutMs: 10000,
          name: "getSession",
          critical: true
        }
      );
      
      return session;
    } catch (error) {
      
      return { data: { session: null } };
    }
  },

  // Get the current user with retry mechanism
  async getCurrentUser() {
    
    
    if (!isNetworkReadyForApiCalls()) {
      
      
      try {
        const cachedUserData = localStorage.getItem('santiyex-user-cache');
        if (cachedUserData) {
          
          return JSON.parse(cachedUserData) as User;
        }
      } catch (cacheError) {
        
      }
      
      return null;
    }
    
    try {
      getCurrentUserInProgress = true;
      getCurrentUserLastAttemptTime = Date.now();
      
      if (Capacitor.isNativePlatform()) {
        try {
          const isConnected = await quickConnectivityCheck();
          if (!isConnected) {
            
            const sessionOnlyUser = await this.getUserFromSessionOnly();
            getCurrentUserInProgress = false;
            return sessionOnlyUser;
          }
        } catch (networkError) {
          
        }
      }

      // **OPTIMIZATION 1: Reduced session timeout from 5s to 3s**
      const { data: { session } } = await withTimeout(
        supabase.auth.getSession(), 
        3000, // Reduced from 5000ms
        "getCurrentUser.getSession"
      );
      
      
      if (!session) {
        
        getCurrentUserInProgress = false;
        return null;
      }

      // **OPTIMIZATION 2: Smart background resume detection**
      const isResumingFromBackground = Capacitor.isNativePlatform() && 
                                      (document.visibilityState === 'visible') &&
                                      localStorage.getItem('app-was-backgrounded') === 'true';
                                      
      if (isResumingFromBackground) {
        
        localStorage.removeItem('app-was-backgrounded');
        
        // Return basic user immediately
        const basicUser = {
          id: session.user.id,
          email: session.user.email,
          name: session.user.email?.split('@')[0] || 'User',
        } as User;
        
        // Fetch complete data in background
        this.fetchAndCacheUserInBackground(session.user.id);
        
        getCurrentUserInProgress = false;
        return basicUser;
      }

      // **OPTIMIZATION 3: Try cache first, then database**
      try {
        const cachedUserString = localStorage.getItem('santiyex-user-cache');
        if (cachedUserString) {
          const cachedUser = JSON.parse(cachedUserString) as User;
          if (cachedUser.id === session.user.id) {
            
            getCurrentUserInProgress = false;
            
            // Verify cache freshness in background
            setTimeout(() => {
              this.refreshUserDataInBackground(session.user.id);
            }, 1000);
            
            return cachedUser;
          }
        }
      } catch (cacheError) {
        
      }
      
      // **OPTIMIZATION 4: Reduced database timeout from 7s to 4s**
      try {
        const userQueryResult = await withTimeout(
          supabase
            .from('users')
            .select('*')
            .eq('id', session.user.id)
            .single(),
          4000, // Reduced from 7000ms
          "getCurrentUser.fetchUserData"
        );

        const { data, error } = userQueryResult;

        if (!data) {
          
          getCurrentUserInProgress = false;
          return {
            id: session.user.id,
            email: session.user.email,
            name: session.user.email?.split('@')[0] || 'User',
          } as User;
        }
        
        const userData = {
          id: data.id,
          email: data.email,
          name: data.name || data.email?.split('@')[0] || 'User',
          company: data.company,
          avatar: data.avatar,
          role: data.role,
          plan: data.plan,
          trialDaysRemaining: data.trial_days_remaining,
          is_dev_admin: ('is_dev_admin' in data && data.is_dev_admin) ? data.is_dev_admin : false
        } as User;
        
        // **OPTIMIZATION 5: Async cache update**
        setTimeout(() => {
          try {
            localStorage.setItem('santiyex-user-cache', JSON.stringify(userData));
          } catch (cacheError) {
            
          }
        }, 0);
        
        getCurrentUserInProgress = false;
        return userData;
      } catch (error) {
        
        getCurrentUserInProgress = false;
        return {
          id: session.user.id,
          email: session.user.email,
          name: session.user.email?.split('@')[0] || 'User',
        } as User;
      }
    } catch (error) {
      
      getCurrentUserInProgress = false;
      return null;
    } finally {
      getCurrentUserInProgress = false;
    }
  },

  // **NEW: Background refresh method**
  async refreshUserDataInBackground(userId: string) {
    try {
      
      const { data } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
        
      if (data) {
        const userData = {
          id: data.id,
          email: data.email,
          name: data.name || data.email?.split('@')[0] || 'User',
          company: data.company,
          avatar: data.avatar,
          role: data.role,
          plan: data.plan,
          trialDaysRemaining: data.trial_days_remaining,
          is_dev_admin: ('is_dev_admin' in data && data.is_dev_admin) ? data.is_dev_admin : false
        } as User;
        
        localStorage.setItem('santiyex-user-cache', JSON.stringify(userData));
        
      }
    } catch (error) {
      
    }
  },

  // Retry wrapper for getCurrentUser with multiple attempts
  async getRetryingCurrentUser(maxRetries = 3, retryDelayMs = 2000) {
    
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        
        const user = await this.getCurrentUser();
        if (user) {
          
          return user;
        }
        
        
        
        if (attempt < maxRetries) {
          
          await new Promise(resolve => setTimeout(resolve, retryDelayMs));
        }
      } catch (error) {
        
        lastError = error;
        
        if (attempt < maxRetries) {
          
          await new Promise(resolve => setTimeout(resolve, retryDelayMs));
        }
      }
    }
    
    
    return null;
  },

  // Helper function to get user from session only (no database calls)
  async getUserFromSessionOnly() {
    try {
      const { data: { session } } = await withTimeout(
        supabase.auth.getSession(),
        5000,
        "getUserFromSessionOnly"
      );
      
      if (!session) {
        return null;
      }
      
      return {
        id: session.user.id,
        email: session.user.email,
        name: session.user.email?.split('@')[0] || 'User',
      } as User;
    } catch (error) {
      
      return null;
    }
  },
  
  // Fetch and cache user data in the background without blocking the UI
  async fetchAndCacheUserInBackground(userId: string) {
    try {
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (data && !error) {
        
        const userData = {
          id: data.id,
          email: data.email,
          name: data.name || data.email?.split('@')[0] || 'User',
          company: data.company,
          avatar: data.avatar,
          role: data.role,
          plan: data.plan,
          trialDaysRemaining: data.trial_days_remaining,
          is_dev_admin: ('is_dev_admin' in data && data.is_dev_admin) ? data.is_dev_admin : false
        } as User;
        
        localStorage.setItem('santiyex-user-cache', JSON.stringify(userData));
      }
    } catch (backgroundError) {
      
    }
  },

  // Update user profile data
  async updateProfile(userData: Partial<User>) {
    try {
      const { data: { session } } = await withTimeout(
        supabase.auth.getSession(), 
        10000,
        "updateProfile.getSession"
      );
      
      if (!session) {
        throw new Error('User not authenticated');
      }

      const result = await withTimeout(
        supabase
          .from('users')
          .update(userData)
          .eq('id', session.user.id),
        10000,
        "updateProfile.updateUserData"
      );
      
      if (!result?.error) { 
        await logActivity(
          session.user.id,
          ActivityActions.UPDATE_PROFILE,
          {
            changes: Object.keys(userData).join(', ')
          }
        );
      }
      
      return result;
    } catch (error) {
      
      throw error;
    }
  },
  
  // Reset password (Assuming Supabase might use OTP for phone, needs further investigation/implementation)
  // For now, let's assume a direct email reset is still needed or adapt later.
  // We might need a new method like `sendPasswordResetOtp(phone: string)`
  // Keeping email reset for now, but flagging that this needs review based on phone-first strategy.
  async resetPassword(email: string, captchaToken?: string): Promise<{ success: boolean }> {
    try {
      const resetOptions: { redirectTo: string; captchaToken?: string } = {
        redirectTo: window.location.origin + '/reset-password',
      };
      if (captchaToken) {
        resetOptions.captchaToken = captchaToken;
      }

      const { error } = await supabase.auth.resetPasswordForEmail(email, resetOptions);
      
      if (error) {
        
        return { success: false };
      }
      
      const { data } = await supabase
        .from('users')
        .select('id')
        .eq('email', email)
        .single();
        
      if (data?.id) {
        await logActivity(
          data.id,
          ActivityActions.RESET_PASSWORD,
          { email }
        );
      }
      
      return { success: true };
    } catch (error) {
      
      return { success: false };
    }
  },

  // Get user profile by phone number
  async getUserProfileByPhone(phone: string): Promise<Pick<UserProfile, 'id' | 'name' | 'email' | 'company' | 'phone' | 'selected_role'> | null> {
    
    type UserProfilePhoneData = Pick<UserProfile, 'id' | 'name' | 'email' | 'company' | 'phone' | 'selected_role'>;
    try {
      // Explicitly type the expected response structure
      // Revert back to passing an async function to safeApiCall
      const { data, error } = await safeApiCall<PostgrestSingleResponse<UserProfilePhoneData>>(
        async () => {
          const result = await supabase
            .from('users')
            .select('id, name, email, company, phone, selected_role')
            .eq('phone', phone)
            .limit(1)
            .single();
          // Cast the result to the expected type
          return result as PostgrestSingleResponse<UserProfilePhoneData>;
        },
        {
          timeoutMs: 10000,
          name: "getUserProfileByPhone",
          critical: false,
        }
      );

      if (error) {
        // Use PostgrestError type guard if possible, otherwise check properties
        if (error && typeof error === 'object' && 'code' in error && error.code === 'PGRST116') {
          
          return null;
        }
        
        throw error;
      }

      
      return data;

    } catch (error) {
      
      throw error;
    }
  },

  // --- NEW Email Password Reset Functions ---

  // Send password reset OTP via Email using resetPasswordForEmail
  async sendPasswordResetOtp(email: string, captchaToken?: string) {
    
    // Use resetPasswordForEmail to trigger the correct email template.
    // Ensure your Supabase "Reset Password" email template includes {{ .Token }}
    const { error } = await safeApiCall(
      // Use resetPasswordForEmail - redirectTo is not needed as we handle OTP in-app
      () =>
        captchaToken
          ? supabase.auth.resetPasswordForEmail(email, { captchaToken })
          : supabase.auth.resetPasswordForEmail(email),
      {
        timeoutMs: 20000,
        name: "sendPasswordResetOtp",
        critical: true
      }
    );

    if (error) {
       
    } else {
       
    }

    // Return success boolean based on error presence
    // Note: resetPasswordForEmail itself doesn't return data, only error or null
    return { success: !error, error: error };
  },

  // Verify the password reset OTP
  async verifyPasswordResetOtp(email: string, token: string) {
    
    const result = await safeApiCall(
      () => supabase.auth.verifyOtp({
        email: email,
        token: token,
        type: 'recovery' // Use 'recovery' type for password reset OTPs
      }),
      {
        timeoutMs: 15000,
        name: "verifyPasswordResetOtp",
        critical: true
      }
    );

    if (result.error) {
       
    } else {
       
    }

    // Returns { data: { user, session }, error }
    return result;
  },

  // Update the user's password (must be called after successful OTP verification)
  async updateUserPassword(newPassword: string) {
    
    // Assumes the user is authenticated via verifyOtp before calling this
    const result = await safeApiCall(
      () => supabase.auth.updateUser({
        password: newPassword
      }),
      {
        timeoutMs: 15000,
        name: "updateUserPassword",
        critical: true // Should happen immediately after verification
      }
    );

    if (result.error) {
       
    } else {
       
       // Log activity after successful password update
       // Commented out because ActivityActions.UPDATE_PASSWORD doesn't exist
       // if (result.data?.user?.id) {
       //   await logActivity(
       //     result.data.user.id,
       //     ActivityActions.UPDATE_PASSWORD, // Assuming you have this action
       //     { method: 'email_otp_reset' }
       //   );
       // }
    }

    // Returns { data: { user }, error }
    return result;
  },

  // General purpose OTP verification for all types (signup, recovery, etc.)
  async verifyOtp(params: {
    email?: string,
    phone?: string,
    token: string, 
    type: 'signup' | 'recovery' | 'email_change' | 'sms' | 'phone_change'
  }) {
    const { email, phone, token, type } = params;
    const identifier = email ? `email: ${email}` : `phone: ${phone}`;
    
    
    // Create the params object according to the verification type
    let verifyParams: any;
    
    if (email) {
      verifyParams = {
        email,
        token,
        type: type as 'signup' | 'recovery' | 'email_change' // Email OTP types
      };
    } else if (phone) {
      verifyParams = {
        phone,
        token,
        type: type as 'sms' | 'phone_change' // Phone OTP types
      };
    } else {
      
      return { data: null, error: new Error("Either email or phone must be provided") };
    }
    
    const result = await safeApiCall(
      () => supabase.auth.verifyOtp(verifyParams),
      {
        timeoutMs: 15000,
        name: `verifyOtp_${type}`,
        critical: true
      }
    );

    if (result.error) {
       
    } else {
       
    }

    return result;
  },

  // --- END Email Password Reset Functions ---

  // --- Phone Password Reset OTP Function (kept for reference/potential future use) ---
  async sendPasswordResetOtpForPhone(phone: string, captchaToken?: string) {
    
    // IMPORTANT: supabase.auth.signInWithOtp is used here as a placeholder.
    // This might log the user in or require specific backend configuration
    // (e.g., custom SMS template type, Edge Function) to work correctly
    // *solely* for password reset without unintended side effects.
    // Verify your Supabase setup for handling password reset OTPs via phone.
    const result = await safeApiCall(
      () => supabase.auth.signInWithOtp({
        phone: phone,
        options: {
          // shouldCreateUser: false, // Might be useful if your backend supports it
          captchaToken: captchaToken,
          // channel: 'sms', // Default is usually sms
        },
      }),
      {
        timeoutMs: 20000,
        name: "sendPasswordResetOtpForPhone",
        critical: true // Critical as it's a user-initiated action
      }
    );

    // Log activity if possible (might need user ID, which we don't have here easily)
    // Consider logging this on the backend/Edge Function side if needed.

    if (result.error) {
       
    } else {
        // Data might be null or contain user info depending on backend
    }

    return result; // Return the full result { data, error }
  },

  // Add deleteUser API method to authAPI using proper Supabase Admin API
  async deleteAccount(): Promise<{ success: boolean; error?: Error }> {
    
    
    try {
      // Get current user session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        
        return { success: false, error: new Error('No active session') };
      }
      
      
      
      // Call the edge function that uses Supabase Admin API
      const { data, error } = await supabase.functions.invoke('delete-account', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      if (error) {
        
        return { success: false, error: new Error(`Edge function error: ${error.message}`) };
      }
      
      if (!data?.success) {
        
        return { success: false, error: new Error(data?.error || 'Unknown error from edge function') };
      }
      
      
      
      // Sign the user out from client after successful deletion
      
      await supabase.auth.signOut();
      
      
      return { success: true };
    } catch (error) {
      
      return { 
        success: false, 
        error: error instanceof Error ? error : new Error('Unknown error during account deletion') 
      };
    }
  }
};

// Export all API services
export * from './projects';
export * from './people';
export * from './entries';
export * from './materials';
export * from './reports';
export * from './units';
export * from './construction-stages';
export * from './currency';
export * from './support';  // Export our new support API
export * from './export';  // Export our new export API
export * from './admin';  // Export our new admin API
export * from './notifications';
